import { PIXI } from '@colonist/pixi';
import { uiGameManager } from '../../EntryShared.ts';
import { TextView } from '../../ui_canvas_engine/TextView.ts';
import { GameColor } from '@colonist/ui-game';
import { GameFont } from '../../GameFont.ts';
import { UIGameToolTipBgView } from './UIGameToolTipBgView.ts';
import { UIGameToolTipPointerView } from './UIGameToolTipPointerView.ts';
import { clientI18n } from '../../ClientI18n.ts';
import { SectionBgView } from '../../ui_canvas_engine/SectionBgView.ts';
import { isOverlapping } from '../../ui_utils/PixiUtils.ts';
export class UIGameToolTipView extends PIXI.Container {
    increaseMaxWidthIfNeeded() {
        if (this.isProgressCardTooltip) return;
        if (!this.hasBodyPart) {
            this.maxTipWidth *= 2;
            return;
        }
        const words = this.bodyParagraphs.map((text)=>clientI18n.t(text.key, text.options)).join(' ');
        const wordsArray = words.split(' ');
        const wordCount = wordsArray.length;
        if (wordCount > 40) {
            this.maxTipWidth *= 2;
        } else if (wordCount > 25) {
            this.maxTipWidth *= 1.5;
        } else if (wordCount > 20) {
            this.maxTipWidth *= 1.25;
        }
    }
    createStyledTexts() {
        this.titleTextView = this.getText(this.title, this.titleFont, this.horizontalPadding);
        this.titleTextView.makeBold();
        for (const [index, paragraph] of this.bodyParagraphs.entries()){
            const prefix = this.bodyPrefixes[index];
            if (prefix == undefined) {
                this.bodyPrefixTextsView.push(undefined);
                this.bodyFirstLineTextsView.push(undefined);
                const bodyTextView = this.getText(paragraph, this.bodyFont, this.horizontalPadding);
                bodyTextView.changeStyle({
                    lineHeight: this.bodyLineHeight
                });
                bodyTextView.removeBold();
                this.bodyTextsView.push(bodyTextView);
                continue;
            }
            const prefixTextView = this.getText(prefix.text, this.bodyFont, this.horizontalPadding, prefix.color);
            prefixTextView.changeStyle({
                lineHeight: this.bodyLineHeight,
                wordWrap: false
            });
            prefixTextView.makeSemiBold();
            this.bodyPrefixTextsView.push(prefixTextView);
            const fullBodyText = clientI18n.t(paragraph.key, paragraph.options);
            const totalAvailableWidth = this.maxTipWidth - this.horizontalPadding * 2;
            const firstLineMaxWidth = totalAvailableWidth - prefixTextView.width - this.prefixBodyGap;
            const { firstLine, rest } = this.splitBodyForInlinePrefix(fullBodyText, firstLineMaxWidth);
            const firstLineView = this.createRawBodyText(firstLine, this.horizontalPadding + prefixTextView.width + this.prefixBodyGap, totalAvailableWidth, false);
            this.bodyFirstLineTextsView.push(firstLineView);
            const restTextView = this.createRawBodyText(rest, this.horizontalPadding, totalAvailableWidth, true);
            if (rest.length == 0) restTextView.visible = false;
            this.bodyTextsView.push(restTextView);
        }
    }
    getText(paragraph, font, x, color) {
        const availableTextWidth = this.maxTipWidth - this.horizontalPadding * 2;
        const textView = new TextView(paragraph, x, this.titleTopPadding, availableTextWidth, 0, this, font, color !== null && color !== void 0 ? color : this.textColor, false);
        return textView;
    }
    createRawBodyText(text, x, wordWrapWidth, wordWrap) {
        const textView = new TextView({
            key: 'strings:colonistCommon.utils.emptyString'
        }, x, this.titleTopPadding, wordWrapWidth, 0, this, this.bodyFont, this.textColor, false, undefined, false);
        textView.text = text;
        textView.changeStyle({
            lineHeight: this.bodyLineHeight,
            wordWrap: wordWrap,
            wordWrapWidth: wordWrapWidth,
            fontWeight: 'normal'
        }, false);
        return textView;
    }
    splitBodyForInlinePrefix(text, firstLineMaxWidth) {
        const measureStyle = new PIXI.TextStyle({
            fontFamily: 'Open Sans',
            fontSize: GameFont.getResponsiveSize(this.bodyFont),
            fontWeight: 'normal'
        });
        const tokens = text.split(/(\s+)/);
        let firstLine = '';
        let restStartIndex = tokens.length;
        for(let i = 0; i < tokens.length; i++){
            const candidate = firstLine + tokens[i];
            const candidateWidth = PIXI.TextMetrics.measureText(candidate, measureStyle).width;
            if (candidateWidth <= firstLineMaxWidth) {
                firstLine = candidate;
                continue;
            }
            restStartIndex = i;
            break;
        }
        const rest = tokens.slice(restStartIndex).join('').replace(/^\s+/, '');
        return {
            firstLine: firstLine,
            rest: rest
        };
    }
    setDimensions() {
        this.setRealWidth();
        this.setHeights();
    }
    setRealWidth() {
        let maxTextWidth = this.titleTextView.width;
        for (const [index, bodyText] of this.bodyTextsView.entries()){
            var _this_bodyPrefixTextsView_index, _this_bodyFirstLineTextsView_index;
            var _this_bodyPrefixTextsView_index_width;
            const prefixWidth = (_this_bodyPrefixTextsView_index_width = (_this_bodyPrefixTextsView_index = this.bodyPrefixTextsView[index]) === null || _this_bodyPrefixTextsView_index === void 0 ? void 0 : _this_bodyPrefixTextsView_index.width) !== null && _this_bodyPrefixTextsView_index_width !== void 0 ? _this_bodyPrefixTextsView_index_width : 0;
            var _this_bodyFirstLineTextsView_index_width;
            const firstLineWidth = (_this_bodyFirstLineTextsView_index_width = (_this_bodyFirstLineTextsView_index = this.bodyFirstLineTextsView[index]) === null || _this_bodyFirstLineTextsView_index === void 0 ? void 0 : _this_bodyFirstLineTextsView_index.width) !== null && _this_bodyFirstLineTextsView_index_width !== void 0 ? _this_bodyFirstLineTextsView_index_width : 0;
            const firstLineCombinedWidth = prefixWidth + (prefixWidth > 0 ? this.prefixBodyGap : 0) + firstLineWidth;
            const restWidth = bodyText.visible ? bodyText.width : 0;
            const widestLine = Math.max(firstLineCombinedWidth, restWidth);
            if (maxTextWidth < widestLine) maxTextWidth = widestLine;
        }
        this.tooltipW = maxTextWidth + this.horizontalPadding * 2;
    }
    setHeights() {
        this.titleH = this.getTitleHeight();
        this.adjustTitleHeightForNoBody();
        const bodyH = this.getBodyHeight();
        this.tooltipH = this.titleH + bodyH;
    }
    getTitleHeight() {
        if (!this.hasTitle()) return 0;
        return this.titleTextView.height + this.titleTopPadding + this.titleBottomPadding;
    }
    adjustTitleHeightForNoBody() {
        if (!this.hasBodyPart) this.titleH *= 1.2;
    }
    getBodyHeight() {
        if (!this.hasBodyPart) return 0;
        return this.getBodyTextViewHeights() + this.getBodyPaddings();
    }
    getBodyTextViewHeights() {
        let height = 0;
        if (!this.hasTitle()) height += this.titleBottomPadding;
        for (const [index, bodyText] of this.bodyTextsView.entries()){
            height += this.getParagraphHeight(index, bodyText);
        }
        return height;
    }
    getParagraphHeight(index, bodyText) {
        const hasPrefix = this.bodyPrefixTextsView[index] != undefined;
        if (!hasPrefix) return bodyText.height;
        const firstLineHeight = this.bodyLineHeight;
        const restHeight = bodyText.visible ? bodyText.height : 0;
        return firstLineHeight + restHeight;
    }
    getBodyPaddings() {
        const paddingBetweenBodyTexts = (this.bodyTextsView.length - 1) * this.bodyTopPadding;
        return this.bodyTopPadding + paddingBetweenBodyTexts;
    }
    createBg() {
        this.toolTipBgView = new UIGameToolTipBgView(this, this.tooltipW, this.tooltipH, this.cornerRadius, this.backgroundColor);
    }
    createPointer(viewToPlaceNear) {
        const bounds = viewToPlaceNear.getBounds();
        const x = bounds.x - this.x + bounds.width / 2;
        new UIGameToolTipPointerView(this, x, viewToPlaceNear, this.cornerRadius, this.backgroundColor);
    }
    repositionTexts() {
        if (!this.hasBodyPart) {
            this.titleTextView.centerYInParent();
            return;
        }
        this.titleTextView.changeStyle({
            align: 'left'
        });
        const initialBodyYWithTitle = this.titleH + this.bodyTopPadding * 0.1;
        const initialBodyY = this.hasTitle() ? initialBodyYWithTitle : this.bodyTopPadding;
        for (const [index, bodyText] of this.bodyTextsView.entries()){
            const previousBodyTextsHeight = this.getPreviousBodyTextsHeight(index);
            const paragraphY = initialBodyY + previousBodyTextsHeight;
            bodyText.changeStyle({
                align: 'left'
            });
            const prefixView = this.bodyPrefixTextsView[index];
            const firstLineView = this.bodyFirstLineTextsView[index];
            if (prefixView == undefined) {
                bodyText.y = paragraphY;
                continue;
            }
            prefixView.changeStyle({
                align: 'left'
            });
            prefixView.y = paragraphY;
            if (firstLineView != undefined) {
                firstLineView.changeStyle({
                    align: 'left'
                });
                firstLineView.y = paragraphY;
            }
            bodyText.y = paragraphY + this.bodyLineHeight;
        }
    }
    getPreviousBodyTextsHeight(index) {
        return this.bodyTextsView.slice(0, index).reduce((height, text, i)=>height + this.getParagraphHeight(i, text) + this.bodyTopPadding, 0);
    }
    hasTitle() {
        return this.title.key !== 'strings:colonistCommon.utils.emptyString';
    }
    checkOverlapsWithSectionBgViews() {
        const stage = uiGameManager.canvasController.app.stage;
        const toolTipBgBounds = this.toolTipBgView.getBounds();
        for (const child of stage.children){
            if (child instanceof SectionBgView == false) continue;
            if (child.visible == false) continue;
            const childBounds = child.getBounds();
            if (isOverlapping(childBounds, toolTipBgBounds)) return true;
        }
        return false;
    }
    checkOverlapsWithMenuButtons(canvas) {
        const menuButtonsWidth = canvas.itemSize * 0.8;
        const menuButtonsHeight = canvas.itemSize * 2;
        const isOverlappingMenuButtonsX = this.x < menuButtonsWidth;
        const isOverlappingMenuButtonsY = this.y < menuButtonsHeight;
        return isOverlappingMenuButtonsX && isOverlappingMenuButtonsY;
    }
    setCoordinateX(canvas) {
        if (!this.viewToPlaceNear) return;
        const globalBounds = this.viewToPlaceNear.getBounds();
        this.x = globalBounds.x + globalBounds.width / 2 - this.tooltipW / 2;
        const isOverlappingRight = this.x + this.tooltipW > canvas.appW;
        const isOverlappingLeft = this.x < 0;
        if (isOverlappingRight) this.x = canvas.appW - this.tooltipW;
        if (isOverlappingLeft) this.x = 0;
    }
    setCoordinateY(canvas) {
        if (!this.viewToPlaceNear) return;
        const spacingForPointer = this.itemSize / 4;
        const belowY = this.viewToPlaceNear.getBounds().bottom + spacingForPointer;
        const aboveY = this.viewToPlaceNear.getBounds().top - this.tooltipH - spacingForPointer;
        this.y = belowY;
        const isOverlappingBelow = this.checkOverlapsWithSectionBgViews();
        this.y = aboveY;
        const isOverlappingAbove = this.checkOverlapsWithSectionBgViews();
        if (isOverlappingBelow && !isOverlappingAbove) this.y = aboveY;
        else this.y = belowY;
        const isOverlappingBottom = this.y + this.tooltipH > canvas.appH - canvas.bottomViewHeight;
        const isOverlappingTop = this.y < 0;
        if (isOverlappingBottom) this.y = aboveY;
        if (isOverlappingTop) this.y = belowY;
    }
    setCoordinates() {
        if (!this.viewToPlaceNear) return;
        const canvas = uiGameManager.canvasController;
        this.setCoordinateX(canvas);
        this.setCoordinateY(canvas);
        const belowY = this.viewToPlaceNear.getBounds().bottom + this.itemSize / 4;
        if (this.checkOverlapsWithMenuButtons(canvas)) this.y = belowY;
        if (this.checkOverlapsWithMenuButtons(canvas)) this.x = canvas.itemSize * 0.8;
    }
    constructor(viewToPlaceNear, title, bodyParagraphs, developmentCards, bodyPrefixes){
        super(), this.uiGameManager = uiGameManager, this.isProgressCardTooltip = false, this.backgroundColor = GameColor.getColorHexValue(GameColor.Color.Gray0), this.bodyParagraphs = [], this.bodyPrefixes = [], this.bodyTextsView = [], this.bodyPrefixTextsView = [], this.bodyFirstLineTextsView = [], this.hasBodyPart = false, this.textColor = GameColor.getColor(GameColor.Color.GrayScale25), this.itemSize = uiGameManager.canvasController.itemSize, this.cornerRadius = 8, this.prefixBodyGap = 5;
        this.viewToPlaceNear = viewToPlaceNear;
        if (developmentCards) this.isProgressCardTooltip = true;
        this.title = title;
        for (const [index, paragraph] of (bodyParagraphs !== null && bodyParagraphs !== void 0 ? bodyParagraphs : []).entries()){
            if (paragraph.key === 'strings:colonistCommon.utils.emptyString') continue;
            this.bodyParagraphs.push(paragraph);
            this.bodyPrefixes.push(bodyPrefixes === null || bodyPrefixes === void 0 ? void 0 : bodyPrefixes[index]);
        }
        this.hasBodyPart = this.bodyParagraphs.length > 0 || this.isProgressCardTooltip;
        this.setConstants();
        this.increaseMaxWidthIfNeeded();
        this.createStyledTexts();
        this.setDimensions();
        this.createBg();
        this.repositionTexts();
        uiGameManager.canvasController.tooltipApp.stage.addChild(this);
        this.setCoordinates();
        this.createPointer(viewToPlaceNear);
    }
}
